import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Zap, Globe, Users } from 'react-feather'; // Asegúrate de tener estas íconos
import { motion } from 'framer-motion';
import Footer from '../../components/Footer';

export default function AboutUs() {
  const navigate = useNavigate();

  // Scroll al inicio al cargar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { label: "Eventos Gestionados", value: "+10" },
    { label: "Usuarios Activos", value: "1K" },
    { label: "Partner Venues", value: "3" },
    { label: "Ciudades", value: "01" },
  ];

  const features = [
    {
      icon: <Zap size={24} />,
      title: "Tecnología Real-Time",
      desc: "Sin esperas. Tu QR se actualiza en vivo. La data fluye al instante entre la puerta y el organizador."
    },
    {
      icon: <Shield size={24} />,
      title: "Seguridad Primero",
      desc: "Adiós a las listas de papel y los QRs falsos. Nuestro sistema de encriptación garantiza un acceso seguro."
    },
    {
      icon: <Globe size={24} />,
      title: "Cultura & Comunidad",
      desc: "No solo vendemos tickets. Construimos el ecosistema donde la música, el arte y la noche convergen."
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-white selection:text-black overflow-x-hidden">
      
      {/* 1. NOISE TEXTURE (Vital para el look & feel) */}
      <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.04] mix-blend-overlay" 
           style={{ backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")' }}>
      </div>

      {/* 2. BACKGROUND GLOW (Sutil, no invasivo) */}
      <div className="fixed top-[-20%] left-[-10%] w-[500px] h-[500px] bg-white/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[-5%] w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Header / Nav (Botón Volver) */}
      <div className="fixed top-0 left-0 w-full z-40 px-6 py-6 flex justify-between items-center pointer-events-none">
        <button 
            onClick={() => navigate(-1)} 
            className="pointer-events-auto flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest text-white/50 hover:text-white transition-colors group uppercase bg-black/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/5"
        >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Volver
        </button>
      </div>

      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* --- HERO SECTION --- */}
          <motion.div 
            className="mb-24 text-center md:text-left"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-mono text-white/40 border border-white/10 px-3 py-1 rounded-full mb-6 inline-block">
              EST. 2024 — LIMA, PE
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 tracking-tighter text-white leading-[0.9]">
              NIGHTLIFE <br/> <span className="text-white/20">REINVENTED.</span>
            </h1>
            <div className="flex flex-col md:flex-row gap-8 items-start md:items-end justify-between border-t border-white/10 pt-8">
                <p className="text-lg md:text-xl text-white/60 max-w-xl font-light leading-relaxed">
                  AFTR nació de una frustración compartida: la noche era increíble, pero la logística para entrar era un desastre. 
                  Creamos la herramienta que nosotros mismos queríamos usar.
                </p>
                
                {/* Scroll Indicator (Visual decoration) */}
                <div className="hidden md:flex flex-col items-center gap-2 text-white/20">
                    <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                </div>
            </div>
          </motion.div>

          {/* --- STATS GRID --- */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-24 overflow-hidden rounded-2xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {stats.map((stat, i) => (
                <div key={i} className="bg-[#050505] p-6 md:p-8 flex flex-col items-center justify-center text-center group hover:bg-[#0a0a0a] transition-colors">
                    <span className="text-3xl md:text-4xl font-bold text-white mb-1 group-hover:scale-110 transition-transform duration-300">{stat.value}</span>
                    <span className="text-xs font-mono text-white/40 uppercase tracking-widest">{stat.label}</span>
                </div>
            ))}
          </motion.div>

          {/* --- MANIFESTO / FEATURES --- */}
          <div className="grid md:grid-cols-12 gap-12 mb-24">
             {/* Left Column: Title */}
             <div className="md:col-span-4">
                <h2 className="text-3xl font-bold sticky top-32">
                    Nuestra <br/> Filosofía
                </h2>
             </div>

             {/* Right Column: Cards */}
             <div className="md:col-span-8 space-y-8">
                {features.map((item, i) => (
                    <motion.div 
                        key={i}
                        className="group relative bg-white/[0.02] border border-white/5 p-8 rounded-2xl hover:border-white/20 transition-all duration-300"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                    >
                        <div className="absolute top-8 right-8 text-white/20 group-hover:text-white transition-colors">
                            {item.icon}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors">{item.title}</h3>
                        <p className="text-white/50 leading-relaxed font-light group-hover:text-white/70 transition-colors">
                            {item.desc}
                        </p>
                    </motion.div>
                ))}
             </div>
          </div>

          {/* --- TEAM / COMMUNITY CTA --- */}
          <motion.div 
            className="relative rounded-3xl overflow-hidden bg-white/5 border border-white/10 p-8 md:p-16 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-20"></div>
            
            <div className="relative z-10 max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-white text-black rounded-full flex items-center justify-center mx-auto mb-8">
                    <Users size={32} strokeWidth={1.5} />
                </div>
                <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">Únete al Club.</h2>
                <p className="text-lg text-white/50 mb-10 font-light">
                    Estamos construyendo la comunidad más grande de amantes de la vida nocturna en Latinoamérica. ¿Te apuntas?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to="/register"
                        className="px-8 py-4 bg-white text-black font-bold rounded-full hover:scale-105 transition-transform shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)]"
                    >
                        CREAR CUENTA
                    </Link>
                    <Link
                        to="/contact"
                        className="px-8 py-4 bg-transparent border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-colors"
                    >
                        CONTACTAR
                    </Link>
                </div>
            </div>
          </motion.div>

        </div>
      </div>
      <Footer />
    </div>
  );
}